import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import useModalEnter from '../hooks/useModalEnter'
import { MatchBlock } from '../pages/MarketAnalysis'

/* "My Market" — what the reference opens after a header Search User pick
 * (layout bundle: searchAnalysis → b-modal ref="searchAnalysisModal",
 * size "big", title "My Market", hide-footer, body `.search-analysis`).
 *
 * Body, top to bottom (reference DOM, user 2026-09-19):
 *   1. `.row.row5` with three blocks —
 *      · Game Detail  (col-md-3): Username / Account Type / General /
 *        Exposure / Credit Reference — from the picked search row
 *      · User Lock    (col-md-4): Master Name | User Act | Bet Active — the
 *        account's direct parent (one level up), which the search API sends
 *        as `row.parent` (backend, merge e7ec975f); Layout folds it into
 *        `analysis.userLock`
 *      · Game Lock    (col-md-5): S.No | Master Name | Bet Lock | Event Name —
 *        per-event bet locks; [] → "No records found"
 *      All three come from the `analysis` prop (shape documented in
 *      data/searchAnalysis.js). Today Layout builds it from the search row
 *      (real Game Detail + direct parent); when the backend ships
 *      `report/searchanalysis` with the full upline chain / game locks, Layout
 *      passes that response instead and nothing here changes.
 *   2. the account's open market book, one .market-analysis-container per
 *      event with the market tables inside — the per-event block the Market
 *      Analysis page renders (pages/MarketAnalysis MatchBlock), fed by the
 *      same dashboard API scoped to that username. The reference also has
 *      "PLayer Battle" / "Diam 11" casino sections; those feeds do not exist
 *      here.
 *
 * Open/close animation + outside click: hooks/useModalEnter, like every
 * other admin modal. Loading is NOT shown inside — Layout keeps the
 * full-screen loader up until the data is here, then mounts this.
 */
const ACCOUNT_TYPE_LABELS = {
  USER: 'User', AGENT: 'Agent', MASTER: 'Master', SUPERMASTER: 'SuperMaster',
  SUPERADMIN: 'SuperAdmin', COMPANY: 'Company', ADMIN: 'Admin', STAFF: 'Staff', OWNER: 'Owner',
}
const accountType = (t) => {
  const key = String(t || '').toUpperCase()
  if (ACCOUNT_TYPE_LABELS[key]) return ACCOUNT_TYPE_LABELS[key]
  const str = String(t || '')
  return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : ''
}
const num = (v) => (Number(v) || 0).toLocaleString()

// Reference: a green check for "yes", a grey/red one for "no".
const Flag = ({ on }) => <i className={on ? 'fas fa-check-square' : 'fas fa-window-close text-danger'}></i>
// Game Lock's tick is the OUTLINED box (far), User Lock's the filled one —
// reference screenshot, 2026-10-11
const OutlineFlag = ({ on }) => <i className={on ? 'far fa-check-square' : 'far fa-window-close text-danger'}></i>

export default function MyMarketModal({ show, analysis = null, matches = [], onHide, title = 'My Market' }) {
  const detail = analysis?.gameDetail || {}
  const userLock = analysis?.userLock || []
  const gameLock = analysis?.gameLock || []
  const { modalClass, backdropClass, onBackdropClick } = useModalEnter(onHide, show)

  // BootstrapVue marks the body while a modal is open
  useEffect(() => {
    if (!show) return undefined
    document.body.classList.add('modal-open')
    return () => document.body.classList.remove('modal-open')
  }, [show])

  if (!show) return null

  const bvid = '__BVID__my_market'
  return createPortal(
    <div id={`${bvid}___BV_modal_outer_`} style={{ position: 'absolute', zIndex: 1040 }}>
      <div id={bvid} role="dialog" aria-describedby={`${bvid}___BV_modal_body_`} className={modalClass} aria-modal="true" style={{ display: 'block' }} onClick={onBackdropClick}>
        <div className="modal-dialog modal-big">
          <span tabIndex="0"></span>
          <div id={`${bvid}___BV_modal_content_`} tabIndex="-1" className="modal-content">
            <header id={`${bvid}___BV_modal_header_`} className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button type="button" aria-label="Close" className="close" onClick={onHide}>×</button>
            </header>
            <div id={`${bvid}___BV_modal_body_`} className="modal-body">
              <div className="search-analysis">
                {/* Game Detail / User Lock / Game Lock belong to the header
                    search, which passes an `analysis`. Opened from a row's
                    Exposure there is none, and the reference's body is then
                    just the market blocks — its DOM reads
                    `.search-analysis > .market-analysis-container` followed by
                    two empty `<!---->` placeholders (user, 2026-09-23). */}
                {analysis && (
                <div className="row row5">
                  <div className="col-md-3">
                    <div className="block-title"><span>Game Detail</span></div>
                    <div className="analysis-detail">
                      <div className="row row5">
                        <label className="col-md-6"><b>Username</b></label>
                        <div className="col-md-6 text-right"><b title={detail.fullname || detail.username || ''}>{detail.username || '-'}</b></div>
                      </div>
                      <div className="row row5">
                        <label className="col-md-6">Account Type</label>
                        <div className="col-md-6 text-right">{accountType(detail.accountType)}</div>
                      </div>
                      <div className="row row5">
                        <label className="col-md-6">General</label>
                        <div className="col-md-6 text-right">{num(detail.general)}</div>
                      </div>
                      <div className="row row5">
                        <label className="col-md-6">Exposure</label>
                        <div className="col-md-6 text-right">{num(Math.abs(Number(detail.exposure) || 0))}</div>
                      </div>
                      <div className="row row5">
                        <label className="col-md-6">Credit Reference</label>
                        <div className="col-md-6 text-right">{num(detail.creditReference)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="block-title"><span>User Lock</span></div>
                    <div className="search-analysis-table mb-3">
                      <table className="table">
                        <thead>
                          <tr><th>Master Name</th><th className="text-center">User Act</th><th className="text-center">Bet Active</th></tr>
                        </thead>
                        <tbody>
                          {userLock.length === 0 ? (
                            <tr><td colSpan="3" className="text-center">No records found</td></tr>
                          ) : userLock.map((r, i) => (
                            <tr key={`${r.masterName}-${i}`}>
                              <td>{r.masterName}</td>
                              <td className="text-center"><Flag on={r.userActive !== false} /></td>
                              <td className="text-center"><Flag on={r.betActive !== false} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="col-md-5">
                    <div className="block-title"><span>Game Lock</span></div>
                    <div className="search-analysis-table mb-3">
                      <table className="table">
                        <thead>
                          <tr><th>S.No.</th><th>Master Name</th><th className="text-center">Bet Lock</th><th>Event Name</th></tr>
                        </thead>
                        <tbody>
                          {gameLock.length === 0 ? (
                            <tr><td colSpan="4" className="text-center">No records found</td></tr>
                          ) : gameLock.map((r, i) => (
                            <tr key={`${r.masterName}-${r.eventName}-${i}`}>
                              <td>{i + 1}</td>
                              <td>{r.masterName}</td>
                              <td className="text-center"><OutlineFlag on={!!r.betLock} /></td>
                              <td>{r.eventName}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                )}
                <div className="market-analysis-container">
                  {matches.map((m) => <MatchBlock key={m.eventid || m.match_id} match={m} inModal />)}
                </div>
              </div>
            </div>
          </div>
          <span tabIndex="0"></span>
        </div>
      </div>
      <div id={`${bvid}___BV_modal_backdrop_`} className={backdropClass}></div>
    </div>,
    document.body
  )
}
